import {
  Flex,
  FormControl,
  FormLabel,
  Text,
  Select,
  FormErrorMessage,
} from '@chakra-ui/react';
import { RefObject } from 'react';
import AddonInput from '../AddonInput';
import BuilderSwitch from '../BuilderSwitch';
import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import { setFormFieldItemProp } from '../../../../store/redux/slices/form.slice';
import { FormFieldState, FormState } from '../../../../store/types/form';
import { DateFormatState } from '../../../../store/types/form';
import FieldEditIndicator from '../FieldEditIndicator';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import { getFormFieldError } from '../validation/validationUtils';

interface DateFormFieldRefs {
  idRef: RefObject<HTMLDivElement>;
  labelRef: RefObject<HTMLDivElement>;
  dateFormatRef: RefObject<HTMLDivElement>;
  requiredRef: RefObject<HTMLDivElement>;
}

const DateFormField = ({
  fieldData,
  fieldIndex,
  templateFieldOptions,
  selectedByOtherFormFields,
  handleFocus,
  handleConnectToPassField,
  validateSection,
  form,
  markTouched,
  fieldRefs,
  activeFieldRef,
  isFieldBeingEdited,
}: {
  fieldData: FormFieldState;
  fieldIndex: number;
  templateFieldOptions: { id: string; label: string }[];
  selectedByOtherFormFields: Set<string>;
  handleFocus: (path: string) => void;
  handleConnectToPassField: (value: string) => void;
  validateSection: (context?: object, validationContext?: object) => void;
  form: FormState;
  markTouched: (field: 'id' | 'label' | 'pattern' | 'message') => void;
  fieldRefs: DateFormFieldRefs;
  activeFieldRef: RefObject<HTMLDivElement> | null;
  isFieldBeingEdited: boolean;
}) => {
  const dispatch = useAppDispatch();
  const errors = useAppSelector((s) => s.templateErrors);
  const getError = (path: string) => getFormFieldError(errors, path);
  const { idRef, labelRef, dateFormatRef, requiredRef } = fieldRefs;

  return (
    <Flex direction="column" gap={4} mt={8}>
      <FieldEditIndicator
        fieldRef={idRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="115px"
      >
        <FormControl isInvalid={!!getError(`form.formFields.${fieldIndex}.id`)}>
          <FormLabel variant="templateBuilderLabel">
            Connect to pass field
          </FormLabel>
          <Text fontSize="12px" my={4}>
            <em>
              Select a field from your pass to link this form input to. The
              connected value will be passed directly to your pass template.
            </em>
          </Text>
          <Select
            value={fieldData.id}
            onChange={(e) => handleConnectToPassField(e.target.value)}
            onFocus={() => {
              handleFocus(`form.formFields.${fieldIndex}.id`);
              markTouched('id');
            }}
            placeholder="Select a field..."
          >
            {templateFieldOptions.map((tf) => (
              <option
                key={tf.id}
                value={tf.id}
                disabled={
                  tf.id !== 'none' && selectedByOtherFormFields.has(tf.id)
                }
              >
                {tf.id === 'none' ? tf.label : tf.id}
              </option>
            ))}
          </Select>
          {getError(`form.formFields.${fieldIndex}.id`) && (
            <FormErrorMessage
              pl={2}
              mt={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {getError(`form.formFields.${fieldIndex}.id`)}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={labelRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="53px"
      >
        <FormControl
          isInvalid={!!getError(`form.formFields.${fieldIndex}.label`)}
        >
          <FormLabel variant="templateBuilderLabel">Heading</FormLabel>
          <AddonInput
            label="Label"
            placeholder="Enter a label"
            value={fieldData.label || ''}
            onChange={(value) => {
              markTouched('label');
              dispatch(
                setFormFieldItemProp({
                  fieldId: fieldData._clientId ?? fieldData.id,
                  key: 'label',
                  value: value as unknown as string,
                })
              );
              const updatedFormFields = form.formFields.map((f, i) =>
                i === fieldIndex ? { ...f, label: value as string } : f
              );
              validateSection({ ...form, formFields: updatedFormFields });
            }}
            onFocus={() => {
              handleFocus(
                fieldData.id
                  ? `fields.${fieldData.id}.label`
                  : `form.formFields.${fieldIndex}.label`
              );
              markTouched('label');
            }}
            isRequired={!!fieldData.label}
          />
          {getError(`form.formFields.${fieldIndex}.label`) && (
            <FormErrorMessage
              pl={2}
              mt={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {getError(`form.formFields.${fieldIndex}.label`)}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={dateFormatRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="55px"
      >
        <FormControl
          isInvalid={!!getError(`form.formFields.${fieldIndex}.dateFormat`)}
        >
          <FormLabel variant="templateBuilderLabel">Date format</FormLabel>
          <Select
            value={fieldData.dateFormat ?? '%d/%m/%Y'}
            onFocus={() =>
              handleFocus(`form.formFields.${fieldIndex}.dateFormat`)
            }
            onChange={(e) => {
              const value = e.target.value as DateFormatState;
              dispatch(
                setFormFieldItemProp({
                  fieldId: fieldData._clientId ?? fieldData.id,
                  key: 'dateFormat',
                  value,
                })
              );
              const updatedFormFields = form.formFields.map((f, i) =>
                i === fieldIndex ? { ...f, dateFormat: value } : f
              );
              validateSection({ ...form, formFields: updatedFormFields });
            }}
            placeholder="Select a date format..."
          >
            <option value="%d/%m/%Y">dd/mm/yyyy</option>
            <option value="%Y/%m/%d">yyyy/mm/dd</option>
            <option value="%d/%m/%y">dd/mm/yy</option>
            <option value="%B %d, %Y">Month Day, Year</option>
          </Select>
          {getError(`form.formFields.${fieldIndex}.dateFormat`) && (
            <FormErrorMessage
              pl={2}
              mt={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {getError(`form.formFields.${fieldIndex}.dateFormat`)}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={requiredRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="21px"
      >
        <Flex
          direction="row"
          width="100%"
          justifyContent="space-between"
          mt={2}
        >
          <Text>Require field?</Text>
          <BuilderSwitch
            isChecked={fieldData.required}
            onFocus={() =>
              handleFocus(`form.formFields.${fieldIndex}.required`)
            }
            onChange={(e) => {
              const checked = e.target.checked;
              dispatch(
                setFormFieldItemProp({
                  fieldId: fieldData._clientId ?? fieldData.id,
                  key: 'required',
                  value: checked,
                })
              );
              const updatedFormFields = form.formFields.map((f, i) =>
                i === fieldIndex ? { ...f, required: checked } : f
              );
              validateSection({ ...form, formFields: updatedFormFields });
            }}
          />
        </Flex>
      </FieldEditIndicator>
    </Flex>
  );
};

export default DateFormField;
